import React, { useContext } from 'react'

import { Link } from 'react-router-dom'
import { Icon, Image, Header, Grid, Button, Dimmer, SemanticICONS } from 'semantic-ui-react'

import { IProject } from '../../../api/Data'
import { UserContext } from '../../../contexts/UserContext'
import moment from '../../../utils/Moment'
import { convertTextAreaToHTML } from '../../../utils/sharedFunctions'

import ExternalLink from '../../../components/common/ExternalLink'
import UpgradeTag from '../../../components/common/UpgradeTag'
import VideoEncodingCard from '../../../components/common/VideoEncodingCard'

import PlanTabPhoneFrame from './PlanTabPhoneFrame'

const getProjectTimeLeft = (deadline: Date) => {
  let iconName: null | SemanticICONS
  let text
  const today = moment()
  const expiryDate = moment(deadline)

  if (!deadline) {
    iconName = null
    text = null
  } else if (today.isAfter(expiryDate)) {
    iconName = 'calendar times outline'
    text = 'project completed'
  } else {
    iconName = 'calendar check outline'
    text = `${moment(expiryDate).fromNow(true)} to go`
  }

  return (
    <>
      {iconName !== null && (
        <Icon name={iconName} />
      )}
      {text !== null && (
        text
      )}
    </>
  )
}

const PlanTabLandingPagePreview = ({ project }: { project: IProject }) => {
  // Contexts
  const { userProfile } = useContext(UserContext)

  // Props
  const { title, description, deadline, incentive, rules, tips, teamTermsUrl, imageUrl } = project
  const { hasVideo, heroVideoEncodingStatus } = project

  const { teamName, logoAvatarUrl } = userProfile!

  const { hasBrandedUploadPage } = userProfile.plan

  return (
    <>
      <Header as="h3">
        Preview (<ExternalLink url={project.projectUrl}>view live</ExternalLink>){' '}
        {!hasBrandedUploadPage && (
          <UpgradeTag title="Upgrade to add your branding" text="Upgrade to the Collector plan to apply your branding to your landing page"></UpgradeTag>
        )}
      </Header>
      <PlanTabPhoneFrame project={project}>
        <>
          {/* project image and title */}
          <Header as="h3" textAlign="center">
            {title}
          </Header>

          {/* team */}
          <Header as="h5">
            <Image avatar src={logoAvatarUrl} alt="crew" style={{ width: '1.5rem' }} />
            <small>
              <Link to="/my-team">{teamName}</Link>
            </small>
          </Header>

          {hasVideo && ![200, 199].includes(heroVideoEncodingStatus) ? (
            <VideoEncodingCard status={heroVideoEncodingStatus} />
          ) : (
            <>
              {/* image */}
              <Dimmer.Dimmable
                style={{
                  width: 'calc(100% + 2rem)',
                  height: '138px',
                  marginLeft: '-1rem',
                  display: 'flex',
                  backgroundImage: `url(${imageUrl})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                }}
              >
                <Dimmer active={hasVideo} style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
                  <Icon name="play circle outline" size="huge" />
                </Dimmer>
              </Dimmer.Dimmable>
            </>
          )}
          {/* incentive and expiry date */}
          <Grid columns={2} padded="vertically">
            <Grid.Row>
              <Grid.Column>
                {incentive && (
                  <Header.Subheader as="h5">
                    <small>
                      <Icon name="gift" />
                      {incentive}
                    </small>
                  </Header.Subheader>
                )}
              </Grid.Column>
              <Grid.Column>
                <Header.Subheader as="h5">
                  <small>{getProjectTimeLeft(deadline)}</small>
                </Header.Subheader>
              </Grid.Column>
            </Grid.Row>
          </Grid>

          <Button fluid compact size="mini" primary style={{ margin: '0 auto' }}>
            Get Started
          </Button>

          {/* brief */}
          <Header as="h5">
            How to participate:
            <Header.Subheader>
              <small dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(description || '') }} />
            </Header.Subheader>
          </Header>

          {/* rules */}
          <Header as="h5">
            Project rules:
            <Header.Subheader>
              <small dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(rules || '') }} />
            </Header.Subheader>
          </Header>

          {/* tips */}
          <Header as="h5">
            Filming tips:
            <Header.Subheader>
              <small dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(tips || '') }} />
            </Header.Subheader>
          </Header>

          {/* terms and conditions */}
          <Header as="h5">
            Terms & conditions:
            <Header.Subheader>
              <small>{teamTermsUrl}</small>
            </Header.Subheader>
          </Header>
        </>
      </PlanTabPhoneFrame>
    </>
  )
}

export default PlanTabLandingPagePreview
